import React, { useEffect, useState } from "react";
import { getCiHolidayPremiums } from "../../api/competitiveIntelligence";
import { RefetchBar } from "./AnalysisPanel";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { SkeletonText } from "../shared/Skeleton";
import SelectMenu from "../shared/SelectMenu";
import Pagination from "../shared/Pagination";
import { fmtDate, fmtRate, fmtPct, fmtSignedRate } from "./shared";

// Client-side pagination. The page owns the full dataset and we slice it
// here at 15 rows per page — matches RateCalendarPanel's pattern.
const PAGE_SIZE = 15;

// Cap absurd capture-rate values. When the comp premium approaches zero (e.g. competitors
// barely lift their holiday rate) the ratio explodes — we've seen values north of 13,000%
// and 16,000% in the wild. Clamp the displayed value at 999%.
const CAPTURE_CAP = 999;
const fmtCapture = (value) => {
  if (value == null) return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  if (num > CAPTURE_CAP) return `>${CAPTURE_CAP}%`;
  return `${num.toFixed(0)}%`;
};

// Audit pass 2 #3: a bare "—" in the Capture Rate column was confusing — users couldn't
// tell whether the value was missing or whether the metric was structurally undefined for
// this row. Compute a brief reason from the per-row premium fields when the status is
// absent.
const captureNullReason = (row) => {
  const youDown = row.client_premium_dollars != null && row.client_premium_dollars < 0;
  const compDown = row.comp_premium_dollars != null && row.comp_premium_dollars < 0;
  if (youDown && compDown) return "Both you and comp are pricing below standard — no holiday lift to compare.";
  if (youDown) return "Your holiday rate is below your standard rate — no premium to capture.";
  if (compDown) return "Comp set is pricing below standard — capture rate would be negative.";
  return "Comp premium is too small to produce a meaningful ratio.";
};
const NULL_CAPTURE_LABEL = "Not comparable";

// A1 — capture_status is the authoritative field from ci_get_holiday_premiums.
// Values: captured, over_inflated, mutual_discount, counter_positioned, no_premium.
// The old code read premium_capture_rate (always null) so every badge showed
// "Not comparable". This map drives the badge from capture_status instead.
// capture_rate (falling back to premium_capture_rate) supplies the numeric value
// for the captured / over_inflated labels and tooltips.
const captureRateValue = (row) => row.capture_rate ?? row.premium_capture_rate;
const CAPTURE_BADGE = {
  captured: {
    label: (row) => fmtCapture(captureRateValue(row)),
    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    tooltip: null,
  },
  over_inflated: {
    label: () => "500%+",
    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    tooltip: (row) => `Actual: ${fmtCapture(captureRateValue(row))}`,
  },
  mutual_discount: {
    label: () => "—",
    cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    tooltip: () => "Neither side treats this as premium",
  },
  counter_positioned: {
    label: () => "Counter",
    // Visually distinct: indigo + ring so the strategically interesting rows
    // (Thanksgiving, Veterans Day, Spring Break Late) stand out from the pack.
    cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 ring-1 ring-indigo-300 dark:ring-indigo-700",
    tooltip: () => "Strategically interesting — directions disagree",
  },
  no_premium: {
    label: () => "—",
    cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    tooltip: () => "Neither side marks up",
  },
};

// Natural-language join: ["A"] → "A"; ["A","B"] → "A and B"; ["A","B","C"] → "A, B, and C".
const joinNames = (names) => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

// Builds the synthesis line that sits above the table. Counts how many holidays
// the client is leading the comp set on (capture_rate >= 100), names any rows
// where the capture rate is structurally undefined ("Not comparable"). The
// detail-level "why aren't these comparable" lives in the per-row tooltip — we
// don't try to summarise the reason in this headline because the rows can have
// different reasons (your rate below standard vs. comp set below standard etc.)
// and a misleading parenthetical is worse than no parenthetical.
const summarizeRows = (rows) => {
  if (!rows?.length) return null;
  // capture_status drives the counts. "captured" and "over_inflated" both mean the
  // client is extracting more holiday lift than the comp set → leading. Rows where
  // neither side marks up (mutual_discount / no_premium) aren't comparable.
  const leading = rows.filter((row) => row.capture_status === "captured" || row.capture_status === "over_inflated").length;
  const notComparable = rows.filter((row) => ["mutual_discount", "no_premium"].includes(row.capture_status));
  const notComparableNames = notComparable.map((row) => row.date_window_name).filter(Boolean);
  const headline = `Leading on ${leading} of ${rows.length} ${rows.length === 1 ? "holiday" : "holidays"}.`;
  const caveat = notComparableNames.length > 0
    ? `${joinNames(notComparableNames)} ${notComparableNames.length === 1 ? "isn’t" : "aren’t"} comparable.`
    : null;
  // Severity follows the dominant story so the card tint matches the badges below.
  // >= 66% leading → emerald (good), >= 33% → amber (mixed), else neutral.
  const ratio = rows.length > 0 ? leading / rows.length : 0;
  const severity = ratio >= 0.66 ? "good" : ratio >= 0.33 ? "warning" : "neutral";
  return { headline, caveat, severity };
};

// Severity tints for the holiday insight card. Light/dark pairs follow the
// in-house aim/InsightCard convention (bg-X-50 in light, bg-X-950/30 in dark).
const INSIGHT_SEVERITY = {
  good: {
    bg: "bg-emerald-50 dark:bg-emerald-950/30",
    border: "border-emerald-200 dark:border-emerald-900/60",
    icon: "text-emerald-600 dark:text-emerald-400",
    title: "text-emerald-900 dark:text-emerald-100",
  },
  warning: {
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-900/60",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-900 dark:text-amber-100",
  },
  neutral: {
    bg: "bg-gray-50 dark:bg-gray-900/40",
    border: "border-gray-200 dark:border-gray-800",
    icon: "text-gray-500 dark:text-gray-400",
    title: "text-gray-900 dark:text-gray-100",
  },
};

const PanelHeader = ({ categoryLabel, count, sortBy, onSortChange }) => (
  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
        Holiday Premiums
        <InfoTip text="For each holiday: your premium over your own standard rate vs the comp set's premium over their standard rate. Capture rate >100% means you're extracting more holiday lift than the market." />
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        Holiday-by-holiday premium capture vs competitors. <span className="font-medium">Capture rate &gt;100%</span> means you’re extracting more holiday lift than the comp set.
      </p>
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      {categoryLabel && (
        <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
          {categoryLabel}
        </span>
      )}
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {count} {count === 1 ? "holiday" : "holidays"}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-300">Sort:</span>
        <SelectMenu
          ariaLabel="Sort holidays"
          value={sortBy}
          onChange={onSortChange}
          options={[
            { value: "date", label: "Check-in date" },
            { value: "capture_desc", label: "Capture rate (high → low)" },
            { value: "capture_asc", label: "Capture rate (low → high)" },
          ]}
        />
      </span>
    </div>
  </div>
);

const Skeleton = () => (
  <div className="p-5">
    <SkeletonText lines={5} />
  </div>
);

const Empty = ({ message }) => (
  <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">{message}</div>
);

const HolidayRow = ({ row }) => {
  const badgeCfg = row.capture_status ? CAPTURE_BADGE[row.capture_status] : null;
  const tooltip = badgeCfg?.tooltip
    ? badgeCfg.tooltip(row)
    : (row.capture_status ? null : captureNullReason(row));
  return (
    <tr className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
      <td data-label="Holiday" className="pl-5 pr-3 py-2.5 align-top whitespace-nowrap">
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{row.date_window_name || "—"}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {fmtDate(row.check_in)}{row.check_out ? ` → ${fmtDate(row.check_out)}` : ""}
          </div>
        </div>
      </td>
      <td data-label="Your Holiday Rate" className="px-4 py-2.5 align-top whitespace-nowrap text-right">
        <div>
          <div className="text-gray-900 dark:text-gray-100 font-semibold">{fmtRate(row.client_holiday_rate)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">vs {fmtRate(row.client_standard_rate)} standard</div>
        </div>
      </td>
      <td data-label="Your Premium" className="px-4 py-2.5 align-top whitespace-nowrap text-right">
        <div>
          <div className="text-gray-700 dark:text-gray-200 font-semibold">{fmtSignedRate(row.client_premium_dollars)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{fmtPct(row.client_premium_pct)}</div>
        </div>
      </td>
      <td data-label="Comp Holiday Rate" className="px-4 py-2.5 align-top whitespace-nowrap text-right">
        <div>
          <div className="text-gray-700 dark:text-gray-300">{fmtRate(row.comp_avg_holiday_rate)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">vs {fmtRate(row.comp_standard_rate)} standard</div>
        </div>
      </td>
      <td data-label="Comp Premium" className="px-4 py-2.5 align-top whitespace-nowrap text-right">
        <div>
          <div className="text-gray-700 dark:text-gray-200 font-semibold">{fmtSignedRate(row.comp_premium_dollars)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{fmtPct(row.comp_premium_pct)}</div>
        </div>
      </td>
      <td
        data-label="Capture Rate"
        className={`pr-5 pl-4 py-2.5 align-top whitespace-nowrap text-right${tooltip ? " cursor-help" : ""}`}
        title={tooltip ?? undefined}
      >
        {badgeCfg ? (
          <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-bold tabular-nums ${badgeCfg.cls}`}>
            {badgeCfg.label(row)}
          </span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400 italic">
            {NULL_CAPTURE_LABEL}
          </span>
        )}
      </td>
    </tr>
  );
};

const HolidayPremiumsPanel = ({ clientId, categoryCode }) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const [sortBy, setSortBy] = useState("date");
  const [page, setPage] = useState(1);

  const apiCategory = categoryCode || "rv";

  useEffect(() => {
    let cancelled = false;
    // Keep prior `data` during refetch so the table doesn't blank to a
    // skeleton on category switch.
    setState((prev) => ({ loading: true, error: "", data: prev.data }));
    getCiHolidayPremiums(clientId, { categoryCode: apiCategory })
      .then((resp) => {
        if (cancelled) return;
        const payload = resp?.data ?? resp ?? [];
        setState({ loading: false, error: "", data: Array.isArray(payload) ? payload : [] });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err?.message || "Failed to load", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, apiCategory]);

  const rows = (state.data || []).slice().sort((rowA, rowB) => {
    const rateA = captureRateValue(rowA);
    const rateB = captureRateValue(rowB);
    if (sortBy === "capture_desc") return (rateB ?? -Infinity) - (rateA ?? -Infinity);
    if (sortBy === "capture_asc")  return (rateA ?? Infinity) - (rateB ?? Infinity);
    return (rowA.check_in || "").localeCompare(rowB.check_in || "");
  });
  const summary = summarizeRows(rows);

  // Slice the full sorted dataset for the current page. The summary and the
  // PanelHeader count above still reflect `rows` (the full set) — only the
  // table body renders `pageRows`.
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);

  // Reset to page 1 whenever the sort order or the category tab changes so the
  // user doesn't land mid-way through a newly-reordered list (matches
  // RateCalendarPanel). apiCategory changes trigger a refetch, which swaps in a
  // fresh dataset — page 1 is the right landing spot.
  useEffect(() => { setPage(1); }, [sortBy, apiCategory]);
  // Clamp the page if a refetch shrank the dataset below the current page.
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <PanelHeader
        categoryLabel={apiCategory.toUpperCase()}
        count={rows.length}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      {state.loading && state.data == null && !state.error && <Skeleton />}
      {state.error && (
        <ErrorState variant="inset" message={state.error} />
      )}
      {!state.error && state.data != null && rows.length === 0 && (
        <Empty message="No holiday premium data for this category yet." />
      )}
      {!state.error && state.data != null && rows.length > 0 && (
        <div className="relative">
          {state.loading && <RefetchBar />}
          {summary && (() => {
            const severityStyle = INSIGHT_SEVERITY[summary.severity] ?? INSIGHT_SEVERITY.neutral;
            return (
              <div className="px-5 pt-3 pb-3">
                <div className={`rounded-lg border ${severityStyle.border} ${severityStyle.bg} p-3 flex items-start gap-3`}>
                  <svg
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${severityStyle.icon}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${severityStyle.title}`}>{summary.headline}</div>
                    {summary.caveat && (
                      <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{summary.caveat}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          <div
            className={`overflow-x-auto responsive-card-table-wrapper ${state.loading ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}`}
            aria-busy={state.loading}
          >
          <table className="w-full text-sm responsive-card-table">
            <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="pl-5 pr-3 py-2.5 text-left font-semibold">Holiday</th>
                <th className="px-3 py-2.5 text-right font-semibold">Your Holiday Rate</th>
                <th className="px-3 py-2.5 text-right font-semibold">Your Premium</th>
                <th className="px-3 py-2.5 text-right font-semibold">Comp Holiday Rate</th>
                <th className="px-3 py-2.5 text-right font-semibold">Comp Premium</th>
                <th className="pr-5 pl-3 py-2.5 text-right font-semibold">Capture Rate</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => <HolidayRow key={`${row.check_in}-${index}`} row={row} />)}
            </tbody>
          </table>
          </div>
          {totalPages > 1 && (
            <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={total}
                perPage={PAGE_SIZE}
                label="holidays"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HolidayPremiumsPanel;
